import Modal from "../Modal/Modal";
import CloseIcon from "../../assets/svg/close-icon";
import AvailabilityIcon from "../../assets/svg/availability-icon";
import Button from "../Button/Button";
import CalendarIcon from "../../assets/svg/calendar-icon";
import ThreeDotsVerticalIcon from "../../assets/svg/three-dots-vertical-icon";
import ChevronLeftIcon from "../../assets/svg/chevron-left-icon";
import TrashIcon from "../../assets/svg/trash-icon";
import { DateRange, Range, RangeKeyDict } from "react-date-range";
import {
  useGetAgentBreaksQuery,
  useUpdateAgentBreaksMutation,
  useUpdateSpecificAgentBreakMutation,
} from "../../redux/services/userManagementApi";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  endOfDay,
  startOfDay,
  format,
  differenceInCalendarDays,
  parseISO,
} from "date-fns";
import { customToast } from "../../utils/custom-toast/custom-toast";
import CircularLoader from "../Loader/CircularLoader";

interface AgentAvailabilityLeavesModalProps {
  open: boolean;
  onClose: () => void;
}

interface Leave {
  id: string;
  startDate: string;
  endDate: string;
  start_date: string;
  end_date: string;
  days: number;
}

export const AgentAvailabilityLeavesModal = ({
  open,
  onClose,
}: AgentAvailabilityLeavesModalProps) => {
  const currentUserId = useSelector(
    (state: RootState) => state?.auth.user?._id
  );

  const { data: initialAgentBreaksData, isLoading: isLoadingAgentBreaksData } =
    useGetAgentBreaksQuery(currentUserId || "", {
      skip: !currentUserId,
      refetchOnMountOrArgChange: true,
    });

  const [updateAgentBreaks, { isLoading: isUpdating }] =
    useUpdateAgentBreaksMutation();

  const [deleteSpecificAgentBreak] = useUpdateSpecificAgentBreakMutation();

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const [activeLeaveId, setActiveLeaveId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [popupPosition, setPopupPosition] = useState<"above" | "below">(
    "below"
  );
  const dateRangeRef = useRef<Range | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const disabledDates = useMemo(() => {
    const dates = new Set<string>();

    const getLocalDateString = (utcDateStr: string) => {
      const date = new Date(utcDateStr);
      const userTimezoneOffset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date.getTime() - userTimezoneOffset);
      return localDate.toISOString().split("T")[0];
    };

    initialAgentBreaksData?.upcoming_breaks?.forEach(
      (breakItem: { start_date: string; end_date: string }) => {
        const startLocalStr = getLocalDateString(breakItem?.start_date);
        const endLocalStr = getLocalDateString(breakItem?.end_date);

        const start = new Date(startLocalStr);
        const end = new Date(endLocalStr);

        const current = new Date(start);
        while (current <= end) {
          dates?.add(current?.toISOString()?.split("T")[0]);
          current?.setDate(current?.getDate() + 1);
        }
      }
    );

    return dates;
  }, [initialAgentBreaksData]);

  const upcomingLeaves = useMemo<Leave[]>(() => {
    if (!initialAgentBreaksData?.upcoming_breaks) return [];

    return [...initialAgentBreaksData.upcoming_breaks]
      .sort(
        (a, b) =>
          new Date(a?.start_date).getTime() - new Date(b?.start_date).getTime()
      )
      .map((breakItem) => {
        const startDate = parseISO(breakItem?.start_date);
        const endDate = parseISO(breakItem?.end_date);
        const days = differenceInCalendarDays(endDate, startDate) + 1;

        return {
          id: breakItem?.id,
          startDate: format(startDate, "d MMM yyyy"),
          endDate: format(endDate, "d MMM yyyy"),
          start_date: breakItem?.start_date,
          end_date: breakItem?.end_date,
          days,
        };
      });
  }, [initialAgentBreaksData]);

  const previousLeaves = useMemo<Leave[]>(() => {
    if (!initialAgentBreaksData?.previous_breaks) return [];

    return [...initialAgentBreaksData.previous_breaks]
      .sort(
        (a, b) =>
          new Date(a?.start_date).getTime() - new Date(b?.start_date).getTime()
      )
      .map((breakItem) => {
        const startDate = parseISO(breakItem?.start_date);
        const endDate = parseISO(breakItem?.end_date);
        const days = differenceInCalendarDays(endDate, startDate) + 1;

        return {
          id: breakItem?.id,
          startDate: format(startDate, "d MMM yyyy"),
          endDate: format(endDate, "d MMM yyyy"),
          start_date: breakItem?.start_date,
          end_date: breakItem?.end_date,
          days,
        };
      });
  }, [initialAgentBreaksData]);

  const handleReset = useCallback(() => {
    setDateRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
    dateRangeRef.current = null;
  }, []);

  const handleSelect = useCallback((ranges: RangeKeyDict) => {
    dateRangeRef.current = ranges.selection;
    setDateRange([ranges.selection]);
  }, []);

  const handleSave = useCallback(async () => {
    if (!dateRangeRef.current) {
      return;
    }

    if (
      !dateRange[0] ||
      !currentUserId ||
      !dateRange[0].startDate ||
      !dateRange[0].endDate
    ) {
      return;
    }

    const { startDate, endDate } = dateRange[0];

    const localStartDate = startOfDay(startDate);
    const localEndDate = endOfDay(endDate);

    const startUtc = localStartDate.toISOString();
    const endUtc = localEndDate.toISOString();

    try {
      await updateAgentBreaks({
        userId: currentUserId,
        start_date: startUtc,
        end_date: endUtc,
      }).unwrap();

      customToast("Leave added successfully", "success");
      handleReset();
    } catch (error: unknown) {
      const errorDetail =
        error && typeof error === "object" && "data" in error
          ? (error.data as { detail?: string })?.detail
          : undefined;
      customToast(errorDetail || "Failed to add break", "error");
    }
  }, [dateRange, currentUserId, updateAgentBreaks, handleReset]);

  const renderNavigator = (
    currFocusedDate: Date,
    changeShownDate: (value: string | number | Date) => void
  ) => {
    const monthYear = currFocusedDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    const today = new Date();
    const isCurrentMonth =
      currFocusedDate.getMonth() === today.getMonth() &&
      currFocusedDate.getFullYear() === today.getFullYear();

    return (
      <div className="flex items-center justify-between w-full px-2">
        {!isCurrentMonth && (
          <button
            onClick={() =>
              changeShownDate(
                new Date(
                  currFocusedDate.setMonth(currFocusedDate.getMonth() - 1)
                )
              )
            }
          >
            <ChevronLeftIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
          </button>
        )}
        {isCurrentMonth && <div className="w-5" />}
        <span className="text-base font-semibold text-heading-text dark:text-heading-text-dk">
          {monthYear}
        </span>
        <button
          onClick={() =>
            changeShownDate(
              new Date(currFocusedDate.setMonth(currFocusedDate.getMonth() + 1))
            )
          }
        >
          <ChevronLeftIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk rotate-180" />
        </button>
      </div>
    );
  };

  const handleLeaveAction = (leaveId: string) => {
    if (activeLeaveId === leaveId) {
      setActiveLeaveId(null);
      return;
    }

    const buttonRef = buttonRefs.current[leaveId];
    const scrollContainer = scrollContainerRef.current;

    if (buttonRef && scrollContainer) {
      const buttonRect = buttonRef.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Calculate space relative to the scrollable container
      const spaceBelow = containerRect.bottom - buttonRect.bottom;
      const spaceAbove = buttonRect.top - containerRect.top;

      const popupHeight = 80; // Approximate height of the popup

      // Open above if not enough space below
      if (spaceBelow < popupHeight && spaceAbove >= popupHeight) {
        setPopupPosition("above");
      } else {
        setPopupPosition("below");
      }
    }
    setActiveLeaveId(leaveId);
  };

  const handleDeleteLeave = useCallback(
    async (leave: Leave) => {
      setIsDeleting(true);
      try {
        await deleteSpecificAgentBreak({
          userId: currentUserId || "",
          breakId: leave.id,
          start_date: leave.start_date,
          end_date: leave.end_date,
          is_deleted: true,
        }).unwrap();
        customToast("Leave deleted successfully", "success");
        setActiveLeaveId(null);
      } catch (error) {
        const errorDetail =
          error && typeof error === "object" && "data" in error
            ? (error.data as { detail?: string })?.detail
            : undefined;
        customToast(errorDetail || "Failed to delete leave", "error");
      } finally {
        setIsDeleting(false);
      }
    },
    [currentUserId, deleteSpecificAgentBreak]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        const target = event.target as HTMLElement;
        if (!target.closest("[data-three-dots]")) {
          setActiveLeaveId(null);
        }
      }
    };

    if (activeLeaveId) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [activeLeaveId]);

  return (
    <Modal
      open={open}
      onOpenChange={onClose}
      showHeader={false}
      contentClassName="min-w-[750px] h-[490px] bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk rounded-xl overflow-hidden"
      overlayClassName="!bg-black/80"
      bodyClassName="!p-0 w-full h-full flex flex-col"
      preventOutsideClick={false}
      closeOnEscape={true}
    >
      <div className="flex items-center justify-between gap-2 px-6 pt-5 pb-6 shrink-0">
        <div className="flex items-center gap-2">
          <AvailabilityIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
          <div>
            <div className="text-base font-semibold text-verba-field-label dark:text-verba-field-label-dk">
              Mark Unavailability & Leaves
            </div>
            <div className="text-xs font-normal text-dark-gray-fixed dark:text-dark-gray-fixed-dk">
              Define this agent's time-off and availability.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-4.5 h-4.5 bg-gray-100 dark:bg-tag-gray-dk rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 z-10"
          aria-label="Close modal"
        >
          <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
        </button>
      </div>

      <div className="flex gap-10 px-6 flex-1 min-h-0">
        <div className="shrink-0 w-[311px]">
          <DateRange
            ranges={dateRange}
            onChange={handleSelect}
            months={1}
            direction="horizontal"
            showDateDisplay={false}
            showMonthAndYearPickers={false}
            rangeColors={["#2563eb"]}
            color="#2563eb"
            className="availability-leaves-popup availability-popup-border"
            minDate={new Date()}
            weekdayDisplayFormat="EEEEE"
            monthDisplayFormat="MMMM yyyy"
            navigatorRenderer={renderNavigator}
            disabledDates={Array?.from(disabledDates)?.map(
              (date) => new Date(date)
            )}
          />
        </div>

        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pr-4">
          {upcomingLeaves?.length === 0 && previousLeaves?.length === 0 ? (
            <div className="flex items-start justify-center h-full select-none">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="relative flex items-center justify-center w-[114px] h-[114px]">
                  <div className="absolute border border-gray-25 dark:border-gray-25-dk rounded-[45.288px] w-[108px] h-[108px]" />
                  <div className="absolute border border-gray-50 dark:border-gray-50-dk rounded-[32.014px] w-[86px] h-[86px]" />
                  <div className="absolute border border-gray-100 dark:border-gray-100-dk rounded-[21.082px] w-[64px] h-[64px]" />
                  <AvailabilityIcon className="w-[40px] h-[40px] text-gray-150 dark:text-gray-150-dk" />
                </div>
                <div className="flex flex-col gap-0 items-center text-center">
                  <span className="text-md text-data dark:text-data-dk font-medium">
                    No Leaves yet
                  </span>
                  <span className="text-md text-gray-placeholder dark:text-gray-placeholder-dk">
                    You don't have any upcoming leaves
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <>
              {upcomingLeaves?.length > 0 && (
                <div>
                  <div className="sticky top-0 z-20">
                    <div className="h-[26px] flex items-center gap-1 pl-2 mb-3 bg-table-header-pinned dark:bg-table-header-pinned-dk rounded">
                      <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase">
                        Upcoming Leaves
                      </span>
                      <span className="flex items-center justify-center px-[5px] py-px rounded-sm text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-gray-100 dark:border-gray-100-dk">
                        {upcomingLeaves?.length}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 mb-5">
                    {upcomingLeaves?.map((leave) => (
                      <div
                        key={leave?.id}
                        className="flex items-center justify-between py-2 px-3 border border-border-gray dark:border-border-gray-dk rounded-lg relative"
                      >
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-4.5 h-4.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
                          <span className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk">
                            {leave?.startDate} - {leave?.endDate}
                          </span>
                          <div className="w-6 h-px mx-1.5 bg-border-gray dark:bg-border-gray-dk" />
                          <span className="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                            {leave?.days} day
                            {leave?.days && leave?.days > 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="relative">
                          <button
                            ref={(el) => (buttonRefs.current[leave?.id] = el)}
                            onClick={() => handleLeaveAction(leave?.id)}
                            className="w-[26px] h-[26px] flex items-center justify-center rounded-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                            aria-label="Leave actions"
                            data-three-dots
                          >
                            <ThreeDotsVerticalIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
                          </button>
                          {activeLeaveId === leave?.id && (
                            <div
                              ref={popupRef}
                              className={`absolute right-0 z-50 w-[150px] transition-all duration-200 ${
                                popupPosition === "above" ? "bottom-7" : "top-7"
                              }`}
                              role="menu"
                              aria-label="Leave actions"
                            >
                              <div className="flex flex-col gap-[4px] py-[12px] px-[12px] bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect">
                                <button
                                  onClick={() =>
                                    !isDeleting && handleDeleteLeave(leave)
                                  }
                                  className={`flex items-center gap-[4px] p-[8px] rounded-lg transition-colors duration-200 focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk ${
                                    isDeleting
                                      ? "cursor-wait opacity-80"
                                      : "cursor-pointer"
                                  }`}
                                  role="menuitem"
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? (
                                    <div className="flex items-center gap-1.5 text-verba-red-alert dark:text-verba-red-alert-dk">
                                      <CircularLoader size={14} />
                                      <span className="text-md font-medium">
                                        Deleting
                                      </span>
                                    </div>
                                  ) : (
                                    <>
                                      <TrashIcon className="w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk" />
                                      <span className="text-md font-medium text-verba-red-alert dark:text-verba-red-alert-dk">
                                        Delete Leave
                                      </span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {previousLeaves?.length > 0 && (
                <div>
                  <div className="sticky top-0 z-20">
                    <div className="h-[26px] flex items-center gap-1 pl-2 mb-3 bg-table-header-pinned dark:bg-table-header-pinned-dk rounded">
                      <span className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase">
                        Previous Leaves
                      </span>
                      <span className="flex items-center justify-center px-[5px] py-px rounded-sm text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-gray-100 dark:border-gray-100-dk">
                        {previousLeaves?.length}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    {previousLeaves?.map((leave) => (
                      <div
                        key={leave?.id}
                        className="flex items-center justify-between py-2 px-3 border border-border-gray dark:border-border-gray-dk rounded-lg relative"
                      >
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-4.5 h-4.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
                          <span className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk">
                            {leave?.startDate} - {leave?.endDate}
                          </span>
                          <div className="w-6 h-px mx-1.5 bg-border-gray dark:bg-border-gray-dk" />
                          <span className="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                            {leave?.days} day
                            {leave?.days && leave?.days > 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="relative">
                          <button
                            ref={(el) => (buttonRefs.current[leave?.id] = el)}
                            onClick={() => handleLeaveAction(leave?.id)}
                            className="w-[26px] h-[26px] flex items-center justify-center rounded-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                            aria-label="Leave actions"
                            data-three-dots
                          >
                            <ThreeDotsVerticalIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
                          </button>
                          {activeLeaveId === leave?.id && (
                            <div
                              ref={popupRef}
                              className={`absolute right-0 z-50 w-[150px] transition-all duration-200 ${
                                popupPosition === "above" ? "bottom-7" : "top-7"
                              }`}
                              role="menu"
                              aria-label="Leave actions"
                            >
                              <div className="flex flex-col gap-[4px] py-[12px] px-[12px] bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect">
                                <button
                                  onClick={() =>
                                    !isDeleting && handleDeleteLeave(leave)
                                  }
                                  className={`flex items-center gap-[4px] p-[8px] rounded-lg transition-colors duration-200 focus:outline-none focus:bg-hover-effect dark:focus:bg-hover-effect-dk ${
                                    isDeleting
                                      ? "cursor-wait opacity-80"
                                      : "cursor-pointer"
                                  }`}
                                  role="menuitem"
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? (
                                    <div className="flex items-center gap-1.5 text-verba-red-alert dark:text-verba-red-alert-dk">
                                      <CircularLoader size={14} />
                                      <span className="text-md font-medium">
                                        Deleting
                                      </span>
                                    </div>
                                  ) : (
                                    <>
                                      <TrashIcon className="w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk" />
                                      <span className="text-md font-medium text-verba-red-alert dark:text-verba-red-alert-dk">
                                        Delete Leave
                                      </span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 px-6 pb-5 pt-4 shrink-0">
        <Button
          type="button"
          onClick={onClose}
          innerText="Cancel"
          buttonContainerClass="rounded-[8px] py-[4px] w-[100px] bg-transparent dark:bg-transparent-dk border border-verba-indigo dark:border-verba-indigo-dk"
          innerTextClass="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk"
        />
        <Button
          type="button"
          onClick={handleSave}
          innerText="Apply"
          disabled={
            !dateRangeRef.current ||
            !dateRange[0]?.startDate ||
            !dateRange[0]?.endDate ||
            disabledDates?.has(
              dateRange[0]?.endDate?.toLocaleDateString("en-CA")
            )
          }
          isLoading={isUpdating}
          loaderColor="white"
          buttonContainerClass="rounded-[8px] py-[4px] w-[100px] bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk disabled:!opacity-50 disabled:!cursor-not-allowed"
          innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </Modal>
  );
};

export default AgentAvailabilityLeavesModal;
